import React, { useState } from 'react';
import { AlertTriangle, Settings, Send, AlertCircle, CheckCircle, Loader2, Trash2, Languages, Wrench, Search, ChevronDown, ChevronUp, Eye, Target } from 'lucide-react';
import SimpleTranscribe from './SimpleTranscribe';
import useTranscription from '../hooks/useTranscription';
import { createIndustrialContext } from '../utils/contextLoader';

/**
 * DefectAnalysisPage Component
 * 
 * A modern page for industrial defect analysis with:
 * - Configurable API settings (base URL, agent name, config path)
 * - Text input for breakdown descriptions
 * - API call functionality with comprehensive defect analysis display
 * - Root cause analysis and corrective actions
 * - Error handling and loading states
 * 
 * Follows modern UI/UX design principles with a focus on usability.
 */
const DefectAnalysisPage = () => {
  // Configuration state
  const [config, setConfig] = useState({
    baseUrl: 'https://5378c0ea3300.ngrok-free.app',
    agentName: 'jk_pilger_agent_v2',
    configPath: 'config/jk-gemba.yaml',
    sonioxUrl: 'https://voicefuncappv21150.azurewebsites.net',
    translateToEnglish: false, // Any language to English (default: off)
    enableEndTags: true, // Add or remove end tags (default: on)
    // Optimized Soniox settings for noisy industrial environments with Hinglish
    enableHinglish: true, // Enable Hinglish support for mixed-language operators
    enableLanguageIdentification: false, // PERMANENTLY DISABLED to prevent Hindi/Urdu/Arabic confusion
    enableSpeakerDiarization: false, // Usually single operator per recording
    maxNonFinalTokensDurationMs: 6000, // Balanced for noisy shop floor (6 seconds)
    useIndustrialContext: true, // Use optimized industrial context
    excludeArabicLanguages: true // PERMANENT FIX: Exclude Arabic/Urdu from language hints
  });

  // Transcription hook for voice input
  const {
    transcribedText,
    isRecording,
    error: transcriptionError,
    appendText,
    clearText,
    setRecordingState,
    setError: setTranscriptionError,
    clearError: clearTranscriptionError
  } = useTranscription();

  // Response and UI state
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [expandedDefects, setExpandedDefects] = useState({});

  // Handle configuration changes
  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle transcription updates
  const handleTranscriptionUpdate = (newText) => {
    appendText(newText);
    clearTranscriptionError();
    setError(null);
  };

  // Handle transcription errors
  const handleTranscriptionError = (err) => {
    setTranscriptionError(err);
  };

  // Handle recording state changes
  const handleRecordingStateChange = (recording) => {
    setRecordingState(recording);
  };

  // Handle manual text input changes
  const handleTextChange = (e) => {
    const newText = e.target.value;
    clearText();
    if (newText) {
      appendText(newText);
    }
  };

  // Clear all text
  const handleClearText = () => {
    clearText();
    setError(null);
    clearTranscriptionError();
  };

  // Toggle defect expansion
  const toggleDefectExpansion = (index) => {
    setExpandedDefects(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Handle API call
  const handleAnalyze = async () => {
    if (!transcribedText.trim()) {
      setError('Please enter a breakdown description or record your voice');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      const formData = new FormData();
      formData.append('agent_name', config.agentName);
      formData.append('input', `"${transcribedText}"`);
      formData.append('config_path', config.configPath);
      formData.append('raw_output', 'True');

      const apiResponse = await fetch(`${config.baseUrl}/worker/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });

      if (!apiResponse.ok) {
        throw new Error(`API request failed: ${apiResponse.status} ${apiResponse.statusText}`);
      }

      const responseText = await apiResponse.text();
      
      // Handle the response which might contain ```json wrapper
      let jsonData;
      try {
        jsonData = JSON.parse(responseText);
      } catch (e) {
        const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
        if (jsonMatch) {
          jsonData = JSON.parse(jsonMatch[1]);
        } else {
          throw new Error('Invalid JSON response format');
        }
      }

      setResponse(jsonData);
    } catch (err) {
      setError(err.message || 'An error occurred while analyzing the input');
    } finally {
      setIsLoading(false);
    }
  };

  // Get confidence color
  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'text-green-400 bg-green-900/30';
    if (score >= 0.6) return 'text-yellow-400 bg-yellow-900/30';
    return 'text-red-400 bg-red-900/30';
  };

  // Get curator action color
  const getCuratorActionColor = (action) => {
    switch (action) {
      case 'AUTO_ACCEPT': return 'text-green-400 bg-green-900/30 border-green-500/30';
      case 'REVIEW_REQUIRED': return 'text-yellow-400 bg-yellow-900/30 border-yellow-500/30';
      case 'HUMAN_DECISION': return 'text-orange-400 bg-orange-900/30 border-orange-500/30';
      case 'REJECT': return 'text-red-400 bg-red-900/30 border-red-500/30';
      default: return 'text-blue-400 bg-blue-900/30 border-blue-500/30';
    }
  };

  // Get mapping status color
  const getMappingStatusColor = (status) => {
    if (status.startsWith('NEAR_MATCH')) return 'text-blue-400 bg-blue-900/30';
    if (status === 'NEW_ENTRY') return 'text-purple-400 bg-purple-900/30';
    if (status === 'EXACT_MATCH') return 'text-green-400 bg-green-900/30';
    return 'text-gray-400 bg-gray-900/30';
  };

  // Render defect card
  const renderDefectCard = (defect, index) => {
    const isExpanded = expandedDefects[index];
    
    return (
      <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-3">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                <span className="text-sm font-mono text-red-300 bg-red-900/30 px-3 py-1 rounded-lg">
                  {defect.defect_code}
                </span>
                <div className={`px-3 py-1 rounded-lg text-xs font-medium ${getConfidenceColor(defect.confidence_score)}`}>
                  {Math.round(defect.confidence_score * 100)}% confidence
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{defect.defect_text}</h3>
              <div className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium ${getMappingStatusColor(defect.mapping_status)}`}>
                <Target className="w-3 h-3 mr-1" />
                {defect.mapping_status}
              </div>
            </div>
          </div>

          {/* Primary Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Search className="w-4 h-4 text-orange-400" />
                <h4 className="text-sm font-medium text-orange-300">Primary Root Cause</h4>
              </div>
              <p className="text-xs text-orange-200 mb-1">{defect.root_cause_code}</p>
              <p className="text-sm text-white">{defect.root_cause_text}</p>
            </div>
            
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Wrench className="w-4 h-4 text-blue-400" />
                <h4 className="text-sm font-medium text-blue-300">Primary Corrective Action</h4>
              </div>
              <p className="text-xs text-blue-200 mb-1">{defect.corrective_action_code}</p>
              <p className="text-sm text-white">{defect.corrective_action_text}</p>
            </div>
          </div>

          {/* Curator Action */}
          <div className={`p-3 rounded-lg border mb-4 ${getCuratorActionColor(defect.curator_action)}`}>
            <div className="flex items-center space-x-2 mb-1">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium">Curator Action: {defect.curator_action}</span>
            </div>
          </div>

          {/* Rationale */}
          <div className="bg-gray-900/30 border border-gray-500/30 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Analysis Rationale</h4>
            <p className="text-sm text-gray-100">{defect.rationale}</p>
          </div>

          {/* Expand/Collapse Button */}
          {(defect.possible_root_causes?.length > 0 || defect.possible_corrective_actions?.length > 0) && (
            <button
              onClick={() => toggleDefectExpansion(index)}
              className="flex items-center space-x-2 text-purple-300 hover:text-purple-200 transition-colors"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span className="text-sm">
                {isExpanded ? 'Hide' : 'Show'} Alternative Options
              </span>
            </button>
          )}
        </div>

        {/* Expandable Alternatives */}
        {isExpanded && (
          <div className="border-t border-white/10 p-6 bg-white/5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Alternative Root Causes */}
              {defect.possible_root_causes?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-orange-300 mb-3 flex items-center space-x-2">
                    <Search className="w-4 h-4" />
                    <span>Alternative Root Causes</span>
                  </h4>
                  <div className="space-y-2">
                    {defect.possible_root_causes.map((cause, causeIndex) => (
                      <div key={causeIndex} className="bg-orange-900/10 border border-orange-500/20 rounded-lg p-3">
                        <p className="text-xs text-orange-200 mb-1">{cause.root_cause_code}</p>
                        <p className="text-sm text-white">{cause.root_cause_text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Alternative Corrective Actions */}
              {defect.possible_corrective_actions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-blue-300 mb-3 flex items-center space-x-2">
                    <Wrench className="w-4 h-4" />
                    <span>Alternative Corrective Actions</span>
                  </h4>
                  <div className="space-y-2">
                    {defect.possible_corrective_actions.map((action, actionIndex) => (
                      <div key={actionIndex} className="bg-blue-900/10 border border-blue-500/20 rounded-lg p-3">
                        <p className="text-xs text-blue-200 mb-1">{action.action_code}</p>
                        <p className="text-sm text-white">{action.action_text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl mb-4">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Defect Analysis
            </h1>
            <p className="text-xl text-purple-100">
              AI-powered defect detection with root cause analysis
            </p>
          </div>

          {/* Configuration Panel */}
          <div className="mb-6">
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="flex items-center space-x-2 text-white hover:text-purple-200 transition-colors"
            >
              <Settings className="w-5 h-5" />
              <span>Configuration</span>
            </button>

            {showConfig && (
              <div className="mt-4 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Defect Analysis API URL
                    </label>
                    <input
                      type="text"
                      value={config.baseUrl}
                      onChange={(e) => handleConfigChange('baseUrl', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="https://api.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Rayosense Voice API URL
                    </label>
                    <input
                      type="text"
                      value={config.sonioxUrl}
                      onChange={(e) => handleConfigChange('sonioxUrl', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="https://voice-api.example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Agent Name
                    </label>
                    <input
                      type="text"
                      value={config.agentName}
                      onChange={(e) => handleConfigChange('agentName', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="short_pilger_classifier_agent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Config Path
                    </label>
                    <input
                      type="text"
                      value={config.configPath}
                      onChange={(e) => handleConfigChange('configPath', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="config/jk-gemba.yaml"
                    />
                  </div>
                </div>

                {/* Voice Transcription Settings */}
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-medium text-white mb-3 flex items-center space-x-2">
                    <Languages className="w-4 h-4" />
                    <span>Voice Transcription Settings</span>
                  </h4>

                  {/* Basic Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="translateToEnglish"
                        checked={config.translateToEnglish}
                        onChange={(e) => handleConfigChange('translateToEnglish', e.target.checked)}
                        className="w-4 h-4 text-purple-600 bg-black/20 border-white/10 rounded focus:ring-purple-500/50 focus:ring-2"
                      />
                      <label htmlFor="translateToEnglish" className="text-sm text-white">
                        Translate any language to English
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="enableEndTags"
                        checked={config.enableEndTags}
                        onChange={(e) => handleConfigChange('enableEndTags', e.target.checked)}
                        className="w-4 h-4 text-purple-600 bg-black/20 border-white/10 rounded focus:ring-purple-500/50 focus:ring-2"
                      />
                      <label htmlFor="enableEndTags" className="text-sm text-white">
                        Enable end tags
                      </label>
                    </div>
                  </div>

                  {/* Industrial Environment Optimizations */}
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-medium text-blue-300 mb-3">Industrial Environment Optimizations</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="enableHinglish"
                          checked={config.enableHinglish}
                          onChange={(e) => handleConfigChange('enableHinglish', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-black/20 border-white/10 rounded focus:ring-blue-500/50 focus:ring-2"
                        />
                        <label htmlFor="enableHinglish" className="text-sm text-white">
                          Enable Hinglish support (Hindi + English)
                        </label>
                      </div>
                      <div className="flex items-center space-x-3 opacity-50">
                        <input
                          type="checkbox"
                          id="enableLanguageIdentification"
                          checked={false}
                          disabled={true}
                          className="w-4 h-4 text-gray-600 bg-black/20 border-white/10 rounded cursor-not-allowed"
                        />
                        <label htmlFor="enableLanguageIdentification" className="text-sm text-gray-400">
                          Language identification per token (DISABLED - Permanent Fix)
                        </label>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="enableSpeakerDiarization"
                          checked={config.enableSpeakerDiarization}
                          onChange={(e) => handleConfigChange('enableSpeakerDiarization', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-black/20 border-white/10 rounded focus:ring-blue-500/50 focus:ring-2"
                        />
                        <label htmlFor="enableSpeakerDiarization" className="text-sm text-white">
                          Speaker diarization (multiple operators)
                        </label>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="useIndustrialContext"
                          checked={config.useIndustrialContext}
                          onChange={(e) => handleConfigChange('useIndustrialContext', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-black/20 border-white/10 rounded focus:ring-blue-500/50 focus:ring-2"
                        />
                        <label htmlFor="useIndustrialContext" className="text-sm text-white">
                          Use industrial context (technical terms)
                        </label>
                      </div>
                    </div>

                    {/* Permanent Hindi Script Fix */}
                    {config.enableHinglish && (
                      <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-4 h-4 bg-green-400 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                          <label className="text-sm text-green-200 font-medium">
                            Hindi Script Protection: ACTIVE
                          </label>
                        </div>
                        <p className="text-xs text-green-300">
                          ✅ Permanent fix applied: Arabic/Urdu languages excluded from hints, language identification disabled.
                          Hindi will always appear in Devanagari script (है, ठीक, नहीं) instead of Arabic script (ہ، ٹھیک، نہیں).
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Advanced Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Response Time (ms) - Higher for noisy environments
                      </label>
                      <select
                        value={config.maxNonFinalTokensDurationMs}
                        onChange={(e) => handleConfigChange('maxNonFinalTokensDurationMs', parseInt(e.target.value))}
                        className="w-full p-2 bg-black/20 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      >
                        <option value={3000}>3000ms (Quiet environment)</option>
                        <option value={6000}>6000ms (Noisy shop floor - Recommended)</option>
                        <option value={9000}>9000ms (Very noisy environment)</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <div className="text-xs text-gray-400">
                        <div className="font-medium text-white mb-1">Current Settings:</div>
                        <div>Languages: {config.enableHinglish ? 'English + Hindi' : 'English only'}</div>
                        <div>Response: {config.maxNonFinalTokensDurationMs}ms</div>
                        <div>Context: {config.useIndustrialContext ? 'Industrial' : 'General'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            {/* Input Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-white mb-2">
                Defect Description
              </label>

              {/* Controls Section */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  {/* Transcribe Component with Industrial Optimizations */}
                  <SimpleTranscribe
                    onTranscriptionUpdate={handleTranscriptionUpdate}
                    onError={handleTranscriptionError}
                    onRecordingStateChange={handleRecordingStateChange}
                    size="lg"
                    sonioxUrl={config.sonioxUrl}
                    translateToEnglish={config.translateToEnglish}
                    enableEndTags={config.enableEndTags}
                    languageHints={config.enableHinglish ? ["hi", "en"] : ["en"]}
                    enableLanguageIdentification={false}
                    enableSpeakerDiarization={config.enableSpeakerDiarization}
                    maxNonFinalTokensDurationMs={config.maxNonFinalTokensDurationMs}
                    context={config.useIndustrialContext ? createIndustrialContext(config.enableHinglish) : null}
                    onContextLoaded={(context) => {
                      console.log(`Industrial context loaded: ${context.length} characters`);
                    }}
                    onContextError={(error) => {
                      console.error('Context loading error:', error);
                    }}
                  />

                  {/* Status Indicator */}
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      isRecording ? 'bg-red-400 animate-pulse' : 'bg-gray-400'
                    }`} />
                    <span className={`text-sm font-medium ${
                      isRecording ? 'text-red-300' : 'text-gray-300'
                    }`}>
                      {isRecording ? 'Recording...' : 'Ready'}
                    </span>
                  </div>
                </div>

                {/* Clear Button */}
                <button
                  onClick={handleClearText}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded-lg transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              </div>

              <div className="space-y-4">
                <textarea
                  value={transcribedText}
                  onChange={handleTextChange}
                  placeholder={
                    isRecording
                      ? config.enableHinglish
                        ? "Listening... Describe the defect in English, Hindi, or mixed (Hinglish)!"
                        : "Listening... Describe the defect!"
                      : config.enableHinglish
                        ? "Describe the defect (e.g., 'Bearing overheating' or 'Bearing garam ho raha hai') or click the microphone to record..."
                        : "Describe the defect (e.g., Bearing overheating) or click the microphone to record..."
                  }
                  className="w-full h-32 p-4 bg-black/20 border border-white/10 rounded-xl text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent"
                />
                <div className="flex justify-center">
                  <button
                    onClick={handleAnalyze}
                    disabled={isLoading || !transcribedText.trim()}
                    className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-red-600 to-orange-500 text-white rounded-xl font-medium hover:from-red-700 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-red-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 min-h-[44px]"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    <span>{isLoading ? 'Analyzing...' : 'Analyze Defect'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {(error || transcriptionError) && (
              <div className="mb-6 space-y-2">
                {error && (
                  <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-300">{error}</span>
                  </div>
                )}
                {transcriptionError && (
                  <div className="p-4 bg-orange-900/20 border border-orange-500/30 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-orange-400" />
                    <span className="text-orange-300">Transcription Error: {transcriptionError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Response Display */}
            {response && Array.isArray(response) && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>Defect Analysis Results ({response.length})</span>
                  </h3>
                </div>
                <div className="space-y-6">
                  {response.map((defect, index) => renderDefectCard(defect, index))}
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="mt-6 space-y-4">
              <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                <h3 className="text-sm font-medium text-red-300 mb-2">How to use Defect Analysis:</h3>
                <ul className="text-sm text-red-200 space-y-1">
                  <li>• Describe the defect symptoms manually or click the microphone to record your voice</li>
                  <li>• The microphone turns red while recording - click again to stop</li>
                  <li>• Supports mixed Hindi-English (Hinglish) for Indian operators</li>
                  <li>• Hindi Script Protection: ACTIVE - Hindi always appears in Devanagari (है, ठीक, नहीं)</li>
                  <li>• Optimized for noisy industrial environments with 6-second response time</li>
                  <li>• Click "Analyze Defect" to get AI-powered defect analysis with root causes</li>
                  <li>• Review confidence scores - higher scores are more reliable</li>
                  <li>• Expand cards to see alternative root causes and corrective actions</li>
                  <li>• Pay attention to curator actions for decision guidance</li>
                </ul>
              </div>

              {/* Sample Phrases for Testing */}
              {config.enableHinglish && (
                <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-300 mb-2">Sample defect descriptions to test (Hinglish support enabled):</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <div className="font-medium text-blue-200 mb-1">English Technical:</div>
                      <ul className="text-blue-100 space-y-1">
                        <li>• "Roll bearing failure with overheating"</li>
                        <li>• "Wall thickness variation in product"</li>
                        <li>• "Gearbox bearing overheating issue"</li>
                        <li>• "Mandrel alignment problem"</li>
                      </ul>
                    </div>
                    <div>
                      <div className="font-medium text-blue-200 mb-1">Hinglish Mixed:</div>
                      <ul className="text-blue-100 space-y-1">
                        <li>• "Bearing toot gaya hai aur garam ho raha hai"</li>
                        <li>• "Product mein thickness variation aa raha hai"</li>
                        <li>• "Gearbox ka bearing bahut garam hai"</li>
                        <li>• "Roll alignment theek nahi hai"</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}


            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefectAnalysisPage;
